import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import { useUpdateCurrency } from "../hooks/useCurrencies";

const PROVIDER_OPTIONS = [
  { label: "None", value: "" },
  { label: "Quidax", value: "quidax" },
  { label: "CoinMarketCap", value: "coinmarketcap" },
  { label: "Brails", value: "brails" },
  { label: "Cashramp", value: "cashramp" },
];

const SPREAD_TYPE_OPTIONS = [
  { label: "Fixed", value: "FIXED" },
  { label: "Percentage", value: "PERCENTAGE" },
];

// rateProviders is an ordered array (e.g. ["brails","cashramp","coinmarketcap"]),
// tried in order until one succeeds - edited here as 4 priority slots.
const toSlots = (rateProviders = []) => {
  const slots = ["", "", "", ""];
  rateProviders.forEach((p, i) => {
    if (i < 4) slots[i] = p;
  });
  return slots;
};

const slotsToRateProviders = (slots) => slots.filter((s) => !!s);

const CurrencyEditForm = ({ isOpen, onClose, currency, allCurrencies = [] }) => {
  const [form, setForm] = useState(null);
  const { mutate: updateCurrency, isPending } = useUpdateCurrency();

  React.useEffect(() => {
    if (currency) {
      setForm({
        spreadType: currency.spreadType || "FIXED",
        buySpread: currency.buySpread ?? 0,
        sellSpread: currency.sellSpread ?? 0,
        businessBuySpread: currency.businessBuySpread ?? 0,
        businessSellSpread: currency.businessSellSpread ?? 0,
        conversionFeePercentage: currency.conversionFeePercentage ?? 0,
        businessConversionFeePercentage: currency.businessConversionFeePercentage ?? 0,
        minFundingPlatformFee: currency.minFundingPlatformFee ?? "",
        businessMinFundingPlatformFee: currency.businessMinFundingPlatformFee ?? "",
        minPayoutPlatformFee: currency.minPayoutPlatformFee ?? "",
        businessMinPayoutPlatformFee: currency.businessMinPayoutPlatformFee ?? "",
        fundingPlatformFeeType: currency.fundingPlatformFeeType || "percentage",
        fundingPlatformFeeValue: currency.fundingPlatformFeeValue ?? 1.0,
        businessFundingPlatformFeeType:
          currency.businessFundingPlatformFeeType || "percentage",
        businessFundingPlatformFeeValue: currency.businessFundingPlatformFeeValue ?? 1.5,
        rateProviderSlots: toSlots(currency.rateProviders),
        canConvertTo: currency.canConvertTo || [],
        canConvertFrom: currency.canConvertFrom || [],
        payoutEnabled: currency.payoutEnabled ?? true,
        collectionEnabled: currency.collectionEnabled ?? true,
      });
    }
  }, [currency]);

  if (!isOpen || !currency || !form) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSlotChange = (index, value) => {
    setForm((prev) => {
      const rateProviderSlots = [...prev.rateProviderSlots];
      rateProviderSlots[index] = value;
      return { ...prev, rateProviderSlots };
    });
  };

  const toggleConvertList = (field, code) => {
    setForm((prev) => {
      const list = prev[field] || [];
      const next = list.includes(code) ? list.filter((c) => c !== code) : [...list, code];
      return { ...prev, [field]: next };
    });
  };

  // Empty field means "no minimum" (null), not zero - distinct from an
  // admin explicitly setting a $0 floor.
  const toNullableNumber = (value) => (value === "" ? null : Number(value));

  const otherCurrencyOptions = allCurrencies
    .filter((c) => c.currencyCode !== currency.currencyCode)
    .map((c) => c.currencyCode);

  const handleSave = () => {
    const rateProviders = slotsToRateProviders(form.rateProviderSlots);
    if (rateProviders.length === 0) {
      toast.error("Set at least one rate provider");
      return;
    }

    updateCurrency(
      {
        uuid: currency.uuid,
        payload: {
          spreadType: form.spreadType,
          buySpread: Number(form.buySpread),
          sellSpread: Number(form.sellSpread),
          businessBuySpread: Number(form.businessBuySpread),
          businessSellSpread: Number(form.businessSellSpread),
          conversionFeePercentage: Number(form.conversionFeePercentage),
          businessConversionFeePercentage: Number(form.businessConversionFeePercentage),
          minFundingPlatformFee: toNullableNumber(form.minFundingPlatformFee),
          businessMinFundingPlatformFee: toNullableNumber(form.businessMinFundingPlatformFee),
          minPayoutPlatformFee: toNullableNumber(form.minPayoutPlatformFee),
          businessMinPayoutPlatformFee: toNullableNumber(form.businessMinPayoutPlatformFee),
          fundingPlatformFeeType: form.fundingPlatformFeeType,
          fundingPlatformFeeValue: Number(form.fundingPlatformFeeValue),
          businessFundingPlatformFeeType: form.businessFundingPlatformFeeType,
          businessFundingPlatformFeeValue: Number(form.businessFundingPlatformFeeValue),
          rateProviders,
          canConvertTo: form.canConvertTo,
          canConvertFrom: form.canConvertFrom,
          payoutEnabled: form.payoutEnabled,
          collectionEnabled: form.collectionEnabled,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${currency.currencyCode?.toUpperCase()} updated`);
          onClose();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to update currency");
        },
      }
    );
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="overflow-y-auto mx-4 w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit {currency.currencyCode?.toUpperCase()}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Rate Providers (in priority order)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {form.rateProviderSlots.map((slot, i) => (
                <select
                  key={i}
                  value={slot}
                  onChange={(e) => handleSlotChange(i, e.target.value)}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="" disabled={i === 0}>
                    {i === 0 ? "Primary" : `Fallback ${i}`}
                  </option>
                  {PROVIDER_OPTIONS.filter((o) => o.value).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Tried in order (Primary first); first successful quote wins.
            </p>
          </div>

          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Spread Type
            </label>
            <select
              value={form.spreadType}
              onChange={(e) => handleChange("spreadType", e.target.value)}
              className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {SPREAD_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Buy Spread (Personal)
              </label>
              <input
                type="number"
                step="any"
                value={form.buySpread}
                onChange={(e) => handleChange("buySpread", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Sell Spread (Personal)
              </label>
              <input
                type="number"
                step="any"
                value={form.sellSpread}
                onChange={(e) => handleChange("sellSpread", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Buy Spread (Business)
              </label>
              <input
                type="number"
                step="any"
                value={form.businessBuySpread}
                onChange={(e) => handleChange("businessBuySpread", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Sell Spread (Business)
              </label>
              <input
                type="number"
                step="any"
                value={form.businessSellSpread}
                onChange={(e) => handleChange("businessSellSpread", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Conversion Fee % (Personal)
              </label>
              <input
                type="number"
                step="any"
                value={form.conversionFeePercentage}
                onChange={(e) => handleChange("conversionFeePercentage", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                Conversion Fee % (Business)
              </label>
              <input
                type="number"
                step="any"
                value={form.businessConversionFeePercentage}
                onChange={(e) => handleChange("businessConversionFeePercentage", e.target.value)}
                className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Funding Platform Fee
            </label>
            <p className="mb-2 -mt-1 text-xs text-gray-400">
              The fee charged on a fiat wallet-funding deposit for this currency. The minimum floor below still applies on top, regardless of type.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs text-gray-500">Type (Personal)</label>
                <select
                  value={form.fundingPlatformFeeType}
                  onChange={(e) => handleChange("fundingPlatformFeeType", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="flat">Flat</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">
                  Value (Personal) {form.fundingPlatformFeeType === "percentage" ? "(%)" : ""}
                </label>
                <input
                  type="number"
                  step="any"
                  value={form.fundingPlatformFeeValue}
                  onChange={(e) => handleChange("fundingPlatformFeeValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">Type (Business)</label>
                <select
                  value={form.businessFundingPlatformFeeType}
                  onChange={(e) => handleChange("businessFundingPlatformFeeType", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="flat">Flat</option>
                  <option value="percentage">Percentage</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">
                  Value (Business) {form.businessFundingPlatformFeeType === "percentage" ? "(%)" : ""}
                </label>
                <input
                  type="number"
                  step="any"
                  value={form.businessFundingPlatformFeeValue}
                  onChange={(e) => handleChange("businessFundingPlatformFeeValue", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Minimum Platform Fee
            </label>
            <p className="mb-2 -mt-1 text-xs text-gray-400">
              Floor applied when the calculated platform fee would otherwise be lower. Leave blank for no minimum.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs text-gray-500">Funding (Personal)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="No minimum"
                  value={form.minFundingPlatformFee}
                  onChange={(e) => handleChange("minFundingPlatformFee", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">Funding (Business)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="No minimum"
                  value={form.businessMinFundingPlatformFee}
                  onChange={(e) => handleChange("businessMinFundingPlatformFee", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">Payout (Personal)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="No minimum"
                  value={form.minPayoutPlatformFee}
                  onChange={(e) => handleChange("minPayoutPlatformFee", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">Payout (Business)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="No minimum"
                  value={form.businessMinPayoutPlatformFee}
                  onChange={(e) => handleChange("businessMinPayoutPlatformFee", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex gap-2 items-center text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.payoutEnabled}
                onChange={(e) => handleChange("payoutEnabled", e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
              />
              Payout enabled
            </label>
            <label className="flex gap-2 items-center text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.collectionEnabled}
                onChange={(e) => handleChange("collectionEnabled", e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
              />
              Collection enabled
            </label>
          </div>

          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Can Convert To
            </label>
            <div className="overflow-y-auto p-2 max-h-32 rounded-lg border border-gray-200">
              <div className="grid grid-cols-3 gap-1">
                {otherCurrencyOptions.map((code) => (
                  <label key={code} className="flex gap-1.5 items-center text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.canConvertTo.includes(code)}
                      onChange={() => toggleConvertList("canConvertTo", code)}
                      className="w-3.5 h-3.5 rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="uppercase">{code}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-xs font-bold tracking-wider text-gray-500 uppercase">
              Can Convert From
            </label>
            <div className="overflow-y-auto p-2 max-h-32 rounded-lg border border-gray-200">
              <div className="grid grid-cols-3 gap-1">
                {otherCurrencyOptions.map((code) => (
                  <label key={code} className="flex gap-1.5 items-center text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.canConvertFrom.includes(code)}
                      onChange={() => toggleConvertList("canConvertFrom", code)}
                      className="w-3.5 h-3.5 rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="uppercase">{code}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurrencyEditForm;
